import { useQuery } from '@tanstack/react-query';
import { differenceInDays, subDays } from 'date-fns';
import { Download, RefreshCw, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { ExportTrackerModal } from '../components/ExportTrackerModal';
import { Layout } from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { DatePicker } from '../components/ui/date-picker';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { usePortalConfig } from '../hooks/usePortalConfig';
import {
  endOfDayInTz,
  formatInTimezone,
  getTimezoneShortLabel,
  startOfDayInTz,
  toLocalDateInTz,
} from '../lib/dateUtils';
import { cn } from '../lib/utils';
import { ResolvedColumn } from '../registry/resolvePortalConfig';
import { getDailySummary } from '../services/reportService';
import { useAuthStore } from '../store/authStore';
import { usePreferencesStore } from '../store/preferencesStore';
import { IDailySummaryRow } from '../types';

const fmtAmount = (n: number) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n ?? 0);

const fmtDate = (d: string, timezone = 'UTC') => {
  try {
    return formatInTimezone(new Date(d), timezone, false);
  } catch {
    return d;
  }
};

interface StatCardProps {
  label: string;
  value: string | undefined;
  isLoading: boolean;
  accent?: boolean;
}

const StatCard = ({ label, value, isLoading, accent }: StatCardProps) => (
  <Card className={accent ? 'border-emerald-500/20' : ''}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
    </CardHeader>
    <CardContent>
      {isLoading ? (
        <Skeleton className="h-8 w-28" />
      ) : (
        <span className="text-2xl font-bold" style={accent ? { color: '#10CFA3' } : {}}>
          {value ?? '-'}
        </span>
      )}
    </CardContent>
  </Card>
);

const MONETARY_STAT_IDS = new Set(['totalBet', 'totalWin', 'ggr', 'coinBetAmount']);

function getStatCardValue(totals: any, cardId: string, currency?: string): string | undefined {
  if (!totals) return undefined;
  const value = totals[cardId];
  if (value == null) return undefined;
  if (MONETARY_STAT_IDS.has(cardId)) {
    const prefix = currency ? `${currency} ` : '';
    return `${prefix}${fmtAmount(value)}`;
  }
  return value?.toLocaleString();
}

type DailyColType = 'date' | 'text' | 'amount' | 'accentAmount' | 'integer' | 'percent';

const DAILY_COL_TYPE: Record<string, DailyColType> = {
  date: 'date',
  operator: 'text',
  betAmount: 'amount',
  winAmount: 'amount',
  ggr: 'accentAmount',
  totalBetCount: 'integer',
  coinBetCount: 'integer',
  winBetCount: 'integer',
  losingBetCount: 'integer',
  coinBetAmount: 'amount',
  rtp: 'percent',
};

type DailyCellRenderer = (value: IDailySummaryRow[keyof IDailySummaryRow], colId: string) => React.ReactNode;

const DAILY_CELL_RENDERERS: Record<DailyColType, DailyCellRenderer> = {
  date: (value, colId) => (
    <TableCell key={colId} className="font-medium whitespace-nowrap">
      {fmtDate(value as string)}
    </TableCell>
  ),
  amount: (value, colId) => (
    <TableCell key={colId} className="text-right tabular-nums">
      {fmtAmount(value as number)}
    </TableCell>
  ),
  accentAmount: (value, colId) => (
    <TableCell key={colId} className="text-right tabular-nums font-semibold" style={{ color: '#10CFA3' }}>
      {fmtAmount(value as number)}
    </TableCell>
  ),
  integer: (value, colId) => (
    <TableCell key={colId} className="text-right tabular-nums">
      {(value as number)?.toLocaleString() ?? '-'}
    </TableCell>
  ),
  percent: (value, colId) => (
    <TableCell key={colId} className="text-right tabular-nums">
      {value != null ? `${(value as number).toFixed(2)}%` : '-'}
    </TableCell>
  ),
  text: (value, colId) => <TableCell key={colId}>{String(value ?? '-')}</TableCell>,
};

function getCellValue(row: IDailySummaryRow, col: ResolvedColumn, timezone: string): React.ReactNode {
  const value = row[col.id as keyof IDailySummaryRow];
  const type = DAILY_COL_TYPE[col.id] ?? 'text';
  if (type === 'date') {
    return (
      <TableCell key={col.id} className="font-medium whitespace-nowrap">
        {fmtDate(value as string, timezone)}
      </TableCell>
    );
  }
  return DAILY_CELL_RENDERERS[type](value, col.id);
}

export const Dashboard = () => {
  const today = new Date();
  const defaultStart = subDays(today, 7);
  const defaultEnd = today;

  const [searchParams, setSearchParams] = useSearchParams();

  const [startDate, setStartDate] = useState<Date | undefined>(() => {
    const param = searchParams.get('startDate');
    return param ? new Date(param) : defaultStart;
  });
  const [endDate, setEndDate] = useState<Date | undefined>(() => {
    const param = searchParams.get('endDate');
    return param ? new Date(param) : defaultEnd;
  });
  const [rangeError, setRangeError] = useState('');
  const [pageNo, setPageNo] = useState(1);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const PAGE_SIZE = 25;

  const { isLoading: configLoading, ...portalConfig } = usePortalConfig();
  const pageConfig = portalConfig.pages.find((p) => p.id === 'dashboard');
  const { maxDateRangeDays: MAX_DAYS } = portalConfig;
  const currencies = useMemo(() => portalConfig.currencies ?? [], [portalConfig.currencies]);

  const { user } = useAuthStore();
  const operatorIds = user?.operatorIds ?? [];

  const { preferredCurrency, setPreferredCurrency, preferredTimezone, setPreferredTimezone } = usePreferencesStore();
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [selectedOperatorId, setSelectedOperatorId] = useState('');
  const timezones = useMemo(() => portalConfig.timezones ?? [], [portalConfig.timezones]);
  const [selectedTimezone, setSelectedTimezone] = useState('');

  useEffect(() => {
    if (currencies.length > 0) {
      setSelectedCurrency((prev) => {
        if (prev) return prev;
        // Use persisted preference if still valid, otherwise fall back to first currency
        return currencies.includes(preferredCurrency) ? preferredCurrency : currencies[0];
      });
    }
  }, [currencies, preferredCurrency]);

  useEffect(() => {
    if (timezones.length > 0) {
      const resolved = timezones.includes(preferredTimezone) ? preferredTimezone : timezones[0];
      setSelectedTimezone(resolved);
      if (resolved !== preferredTimezone) {
        setPreferredTimezone(resolved);
      }
    }
  }, [timezones, preferredTimezone]);

  const activeTimezone = selectedTimezone || 'UTC';
  const hasAppliedFilters = searchParams.has('startDate');
  const appliedFilters: Record<string, unknown> = {
    startDate: searchParams.get('startDate') || startOfDayInTz(defaultStart, activeTimezone).toISOString(),
    endDate: searchParams.get('endDate') || endOfDayInTz(defaultEnd, activeTimezone).toISOString(),
    ...(selectedCurrency && { currency: selectedCurrency }),
    ...(selectedOperatorId && { operatorId: selectedOperatorId }),
    // Read timezone from searchParams so the queryKey only changes once per setSearchParams call,
    // preventing an intermediate query with mismatched timezone and old date boundaries.
    ...((searchParams.get('timezone') || selectedTimezone) && {
      timezone: searchParams.get('timezone') || selectedTimezone,
    }),
  };

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['daily-summary', appliedFilters, pageNo],
    queryFn: () =>
      getDailySummary({
        ...appliedFilters,
        pageNo,
        pageSize: PAGE_SIZE,
      }),
    enabled:
      !configLoading &&
      (currencies.length === 0 || !!selectedCurrency) &&
      (timezones.length === 0 || !!selectedTimezone),
  });

  if (pageConfig && !pageConfig.enabled) {
    const firstEnabled = portalConfig.pages.find((p) => p.enabled && p.id !== 'dashboard');
    return <Navigate to={firstEnabled?.path ?? '/login'} replace />;
  }

  const totals = data?.totals;
  const visibleStatCards = (pageConfig?.statCards ?? []).filter((sc) => sc.visible);
  const tableConfig = pageConfig?.tables?.[0];
  const visibleCols = (tableConfig?.columns ?? []).filter((c) => c.visible);
  const effectiveCurrency = selectedCurrency || data?.data?.[0]?.currency || currencies[0] || 'GBP';

  const applyFilters = () => {
    if (!startDate || !endDate) {
      setRangeError('Please select both start and end dates.');
      return;
    }
    const diffDays = differenceInDays(endDate, startDate);
    if (diffDays < 0) {
      setRangeError('Start date must be before end date.');
      return;
    }
    if (diffDays >= MAX_DAYS) {
      setRangeError(`Date range cannot exceed ${MAX_DAYS} days.`);
      return;
    }
    setRangeError('');
    setPageNo(1);
    const params: Record<string, string> = {
      startDate: startOfDayInTz(startDate, activeTimezone).toISOString(),
      endDate: endOfDayInTz(endDate, activeTimezone).toISOString(),
    };
    if (activeTimezone) params.timezone = activeTimezone;
    setSearchParams(params);
  };

  const clearFilters = () => {
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setSelectedCurrency(currencies[0] ?? selectedCurrency);
    setSelectedTimezone(timezones[0] ?? selectedTimezone);
    setSelectedOperatorId('');
    setRangeError('');
    setPageNo(1);
    setSearchParams({});
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Daily Sales Summary</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Aggregated betting activity grouped by day and operator
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExportModalOpen(true)}
              className="gap-1.5 border border-[#10CFA3] text-[#10CFA3] hover:bg-[#10CFA3] hover:text-white"
            >
              <Download className="w-3.5 h-3.5" />
              Export
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching} className="gap-1.5">
              <RefreshCw className={cn('w-3.5 h-3.5', isFetching && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="pt-5 pb-5">
            <div className="flex items-end gap-4 flex-wrap">
              {/* Date range group - requires Apply */}
              <div className="space-y-2">
                <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">From</Label>
                <DatePicker
                  value={startDate}
                  onChange={(d) => {
                    setStartDate(d);
                    setRangeError('');
                  }}
                  placeholder="Start date"
                  toDate={endDate ?? today}
                />
              </div>
              <div className="space-y-2">
                <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">To</Label>
                <DatePicker
                  value={endDate}
                  onChange={(d) => {
                    setEndDate(d);
                    setRangeError('');
                  }}
                  placeholder="End date"
                  fromDate={startDate}
                  toDate={today}
                />
              </div>
              <Button
                onClick={applyFilters}
                className="text-white font-medium"
                style={{ background: '#8C16C7', border: 'none' }}
              >
                Apply filters
              </Button>
              {hasAppliedFilters && (
                <Button variant="ghost" onClick={clearFilters} className="text-muted-foreground gap-1.5">
                  <X className="w-3.5 h-3.5" />
                  Clear
                </Button>
              )}

              {/* Divider */}
              <div className="w-px self-stretch bg-border mx-2" />

              {/* Instant filters group - no Apply needed */}
              {currencies.length > 0 && (
                <div className="space-y-2">
                  <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Currency
                  </Label>
                  <Select
                    value={selectedCurrency}
                    onValueChange={(val) => {
                      setSelectedCurrency(val);
                      setPreferredCurrency(val);
                      setPageNo(1);
                    }}
                  >
                    <SelectTrigger className="w-28 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {timezones.length > 1 && (
                <div className="space-y-2">
                  <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Timezone
                  </Label>
                  <Select
                    value={selectedTimezone}
                    onValueChange={(val) => {
                      setSelectedTimezone(val);
                      setPreferredTimezone(val);
                      setPageNo(1);
                      // Re-evaluate date boundaries in the new timezone when filters are applied
                      if (searchParams.has('startDate')) {
                        const localStart = toLocalDateInTz(new Date(searchParams.get('startDate')!), activeTimezone);
                        const localEnd = toLocalDateInTz(new Date(searchParams.get('endDate')!), activeTimezone);
                        setStartDate(localStart);
                        setEndDate(localEnd);
                        setSearchParams((prev) => {
                          const next = new URLSearchParams(prev);
                          next.set('startDate', startOfDayInTz(localStart, val).toISOString());
                          next.set('endDate', endOfDayInTz(localEnd, val).toISOString());
                          next.set('timezone', val);
                          return next;
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="w-44 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timezones.map((tz) => (
                        <SelectItem key={tz} value={tz}>
                          {tz} ({getTimezoneShortLabel(tz)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {operatorIds.length >= 2 && (
                <div className="space-y-2">
                  <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Operator
                  </Label>
                  <Select
                    value={selectedOperatorId || 'all'}
                    onValueChange={(val) => setSelectedOperatorId(val === 'all' ? '' : val)}
                  >
                    <SelectTrigger className="w-44 h-9">
                      <SelectValue placeholder="All operators" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {operatorIds.map((op) => (
                        <SelectItem key={op._id} value={op._id}>
                          {op.operatorName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {rangeError && (
              <div
                className="mt-3 text-sm p-3 rounded-lg"
                style={{
                  background: 'rgba(239,68,68,0.08)',
                  color: '#fca5a5',
                  border: '1px solid rgba(239,68,68,0.20)',
                }}
              >
                {rangeError}
              </div>
            )}
            {hasAppliedFilters && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground">Applied:</span>
                <Badge variant="secondary" className="text-xs font-normal">
                  {formatInTimezone(new Date(appliedFilters.startDate as string), activeTimezone, false)} -{' '}
                  {formatInTimezone(new Date(appliedFilters.endDate as string), activeTimezone, false)}
                </Badge>
                {selectedCurrency && (
                  <Badge variant="secondary" className="text-xs font-normal">
                    Currency: {selectedCurrency}
                  </Badge>
                )}
                {timezones.length > 1 && selectedTimezone && (
                  <Badge variant="secondary" className="text-xs font-normal">
                    Timezone: {selectedTimezone}
                  </Badge>
                )}
                {selectedOperatorId && (
                  <Badge variant="secondary" className="text-xs font-normal">
                    Operator:{' '}
                    {operatorIds.find((op) => op._id === selectedOperatorId)?.operatorName ?? selectedOperatorId}
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {visibleStatCards.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {visibleStatCards.map((sc) => (
              <StatCard
                key={sc.id}
                label={sc.label}
                value={getStatCardValue(totals, sc.id, MONETARY_STAT_IDS.has(sc.id) ? effectiveCurrency : undefined)}
                isLoading={isLoading}
                accent={sc.accent}
              />
            ))}
          </div>
        )}

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">{tableConfig?.title ?? 'Breakdown by Date / Operator'}</CardTitle>
                {effectiveCurrency && (
                  <Badge variant="secondary" className="text-xs font-normal">
                    {effectiveCurrency}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageNo === 1 || isFetching}
                  onClick={() => setPageNo((p) => p - 1)}
                  className="h-8 text-xs"
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground px-1">Page {pageNo}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(data?.data?.length ?? 0) < PAGE_SIZE || isFetching}
                  onClick={() => setPageNo((p) => p + 1)}
                  className="h-8 text-xs"
                >
                  Next
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-11 w-full" />
                ))}
              </div>
            ) : isError ? (
              <div
                className="text-sm p-4 rounded-lg"
                style={{
                  background: 'rgba(239,68,68,0.08)',
                  color: '#fca5a5',
                  border: '1px solid rgba(239,68,68,0.20)',
                }}
              >
                Failed to load report data. Please try again.
              </div>
            ) : !data?.data?.length ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                No data found for the selected date range.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {visibleCols.map((col) => (
                        <TableHead key={col.id} className={col.align === 'right' ? 'text-right' : undefined}>
                          {col.label}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.data.map((row, i) => (
                      <TableRow key={i}>{visibleCols.map((col) => getCellValue(row, col, activeTimezone))}</TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ExportTrackerModal
        open={exportModalOpen}
        onOpenChange={setExportModalOpen}
        exportType="daily_summary"
        currentFilters={appliedFilters}
      />
    </Layout>
  );
};
